import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown, X } from "lucide-react";

// Tiny "AI" / "Manual" workflow chip — shown next to any user name in
// dropdowns, selected chips, tables. Users without workflow_type render
// nothing (keeps legacy rows clean).
export function WorkflowTag({ type, size = "sm" }) {
  if (type !== "ai" && type !== "manual") return null;
  const isAI = type === "ai";
  const cls = size === "xs"
    ? "text-[8px] px-1 py-0"
    : "text-[9px] px-1 py-[1px]";
  return (
    <span
      className={`inline-flex items-center rounded font-semibold uppercase tracking-wider border ${cls} ${
        isAI
          ? "border-purple-200 bg-purple-50 text-purple-700"
          : "border-slate-200 bg-slate-50 text-slate-600"
      }`}
      title={isAI ? "AI-assisted workflow" : "Manual workflow"}
      data-testid={`workflow-tag-${type}`}
    >
      {isAI ? "AI" : "Manual"}
    </span>
  );
}

// Compact role chip — used in the picker dropdown & selected chips so the
// user can tell at a glance which "designation" a person holds. Broader
// pickers (iter 35.14) mean any name can be dropped into any slot, so the
// role chip is what disambiguates "is this a CM or a Writer".
const ROLE_SHORT = {
  super_admin: "SUPER",
  admin: "ADMIN",
  department_admin: "DEPT ADM",
  content_manager: "CM",
  team_lead: "TL",
  associate: "ASSOC",
};
export function RoleTag({ role, tl_kind }) {
  if (!role || !(role in ROLE_SHORT)) return null;
  let label = ROLE_SHORT[role];
  if (role === "team_lead" && tl_kind === "publishing") label = "PUB TL";
  return (
    <span
      className="inline-flex items-center rounded font-semibold uppercase tracking-wider border text-[9px] px-1 py-[1px] border-slate-200 bg-white text-slate-500"
      title={role.replace("_", " ")}
    >
      {label}
    </span>
  );
}

// Multi-select combobox for user IDs.
export default function MultiUserSelect({
  value = [], onChange, users, placeholder, disabled, testId,
  variant = "input", children, align = "start",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = useMemo(() =>
    users.filter((u) => !search || u.name.toLowerCase().includes(search.toLowerCase()) || (u.email || "").toLowerCase().includes(search.toLowerCase())),
    [users, search]
  );
  const selected = value.map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const anchor = variant === "trigger" ? (
    <span className={disabled ? "opacity-70 pointer-events-none" : ""} data-testid={testId}>{children}</span>
  ) : (
    <button
      type="button"
      disabled={disabled}
      className={`w-full border border-slate-300 rounded-md min-h-9 px-2 py-1.5 text-left text-sm flex flex-wrap items-center gap-1 ${disabled ? "bg-slate-50 text-slate-500 cursor-not-allowed" : "bg-white hover:border-purple-400"}`}
      data-testid={testId}
    >
      {selected.length === 0 && <span className="text-slate-400">{placeholder}</span>}
      {selected.map((u) => (
        <span key={u.id} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 rounded px-1.5 py-0.5 text-xs font-medium">
          {u.name}
          <WorkflowTag type={u.workflow_type} size="xs" />
          {!disabled && (
            <span onClick={(e) => { e.stopPropagation(); toggle(u.id); }} className="hover:text-purple-900 cursor-pointer">
              <X className="h-3 w-3" />
            </span>
          )}
        </span>
      ))}
      <ChevronDown className="h-4 w-4 text-slate-400 ml-auto" />
    </button>
  );

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>{anchor}</PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-0" onClick={(e) => e.stopPropagation()}>
        <div className="p-2 border-b border-slate-200">
          <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-8 text-sm" />
        </div>
        <div className="max-h-56 overflow-auto">
          {filtered.length === 0 && <div className="p-3 text-xs text-slate-500 text-center">No matches</div>}
          {filtered.map((u) => {
            const isSel = value.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-slate-50 text-left ${isSel ? "bg-purple-50/60" : ""}`}
                data-testid={`${testId}-opt-${u.id}`}
              >
                <div className={`h-4 w-4 rounded border flex items-center justify-center ${isSel ? "bg-purple-600 border-purple-600 text-white" : "border-slate-300"}`}>
                  {isSel && <Check className="h-3 w-3" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-slate-800 truncate">{u.name}</span>
                    <RoleTag role={u.role} tl_kind={u.tl_kind} />
                    <WorkflowTag type={u.workflow_type} />
                  </div>
                  {u.email && <div className="text-xs text-slate-500 truncate">{u.email}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
